/**
 * Version Check Utilities
 *
 * GW-2028: Detect stale app versions after deployment by comparing
 * the build SHA in the running page against the latest deployed index.html.
 * Uses DOMParser for robust HTML meta tag parsing (handles attribute reordering).
 */

/**
 * Read the build-sha meta tag from the current document.
 * Returns null if the tag is missing or has empty content.
 */
export function getCurrentBuildSha(): string | null {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="build-sha"]');
  return meta?.content || null;
}

/**
 * Fetch /index.html from the server (cache-busted) and parse
 * the build-sha meta tag using DOMParser.
 * Returns null on any failure (network, missing tag, etc.).
 */
export async function fetchRemoteBuildSha(): Promise<string | null> {
  try {
    const response = await fetch(`/index.html?_=${Date.now()}`, {
      cache: 'no-store',
    });

    if (!response.ok) {
      return null;
    }

    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const meta = doc.querySelector<HTMLMetaElement>('meta[name="build-sha"]');

    return meta?.content || null;
  } catch {
    return null;
  }
}

/**
 * Compare the running build SHA against the latest deployed version.
 * Returns the new SHA if different from the current, null otherwise.
 */
export async function checkForNewVersion(): Promise<string | null> {
  const currentSha = getCurrentBuildSha();
  const remoteSha = await fetchRemoteBuildSha();

  if (!currentSha || !remoteSha) return null;
  if (currentSha === remoteSha) return null;

  return remoteSha;
}
